import * as React from "react";
import { useTheme } from "styled-components";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  ICombinedJobSchedule,
  IProcessJobSchedule,
  useProcessJobsScheduleApi
} from "src/core/process-job/api/processJobsSchedule.api";
import { Table } from "src/core/dashboard/components/widgets";
import { getProcessJobScheduleColumns } from "src/core/shared/components/tables/processJobScheduleColumns";
import { ActionButton } from "src/library/components/Button";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import { IAction } from "src/library/components/Button/ActionButton";
import { Icons } from "src/library/components/Icon/types";

const defaultPagination: PaginatedParams<IProcessJobSchedule> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "id", "DESC" ] ]
};

/**
 * Process Job Schedule page
 *
 * @returns
 */
const ProcessJobSchedulePage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const TableRef = React.useRef<TableRefProps>(null);
  const [ processJobScheduleResponse, getProcessJobScheduleRequest ] = useProcessJobsScheduleApi("GET_PROCESS_JOB_SCHEDULES");
  const [ updatePJSResponse, updatePJS ] = useProcessJobsScheduleApi ("UPDATE_PROCESS_JOB_SCHEDULE");
  const [ createProcessJobResponse, createProcessJobRequest ] = useProcessJobsScheduleApi("CREATE_PROCESS_JOB_BY_SCHEDULE_ID");
  const [ tableData, setTableData ] = React.useState<ICombinedJobSchedule[]>([]);
  const [ loading, setLoading ] = React.useState(false);
  const terminology = useTerminology();
  const theme = useTheme();

  //if any errors
  React.useEffect(() => {
    if (updatePJSResponse.error) {
      toast.error(`Could not update schedule. ${updatePJSResponse.error.message}`);
    }
  }, [ updatePJSResponse.error ]);

  React.useEffect(() => {
    if (createProcessJobResponse.error) {
      toast.error(`Could not invoke process job. ${createProcessJobResponse.error.message} `);
    }
  }, [ createProcessJobResponse.error ]);

  React.useEffect(() => {
    if (processJobScheduleResponse.error) {
      toast.error(`Could not get schedules. ${processJobScheduleResponse.error.message}.`);
    }
  }, [ processJobScheduleResponse.error ]);

  //toast success messages
  React.useEffect(() => {
    if (createProcessJobResponse.complete && createProcessJobResponse.data) {
      toast.success("Job successfully created! See its status and progress in the job queue");
    }
  }, [ createProcessJobResponse.complete ]);

  //putting display data in its own state - helps with table reloading
  React.useEffect(() => {
    if (processJobScheduleResponse.data?.result)
      setTableData(processJobScheduleResponse.data?.result);
  }, [ processJobScheduleResponse.data?.result, updatePJSResponse.data ]);

  //ensuring data refreshes when a enable/disable request is made.
  React.useEffect(() => {
    getProcessJobScheduleRequest({ pagination: defaultPagination });
  }, [ updatePJSResponse.data, getProcessJobScheduleRequest ]);

  //combining the loading of both requests so the data is fresh
  React.useEffect(() => {
    if (!updatePJSResponse.pending && !processJobScheduleResponse.pending) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [ updatePJSResponse.pending, processJobScheduleResponse.pending ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IProcessJobSchedule>, pagination: PaginatedParams<IProcessJobSchedule>}) => {
      getProcessJobScheduleRequest(params);
    },
    [ getProcessJobScheduleRequest ]
  );

  const columns = React.useMemo(() => {
    return getProcessJobScheduleColumns({
      theme,
      terminology,
      actionButton: info => {
        const parsedInfo = info.row.original;

        //only want the action relative to the status
        const statusAction: IAction = parsedInfo.state === "DISABLED" ? {
          title: "Enable",
          action: () => {
            updatePJS({
              processJobScheduleId: info.row.original.id,
              awsScheduleParams: { state: "ENABLED" },
              processJobSchedule: {}
            });
          },
          icon: Icons.SYNC
        } : {
          title: "Suspend",
          action: () => {
            updatePJS({
              processJobScheduleId: info.row.original.id,
              awsScheduleParams: { state: "DISABLED" },
              processJobSchedule: {}
            });
          },
          icon: Icons.ADVISORY
        };

        //for invoking the process job once.
        const invokeAction: IAction = {
          title: "Trigger Job",
          action: () => {
            createProcessJobRequest(info.row.original.id);
          },
          icon: Icons.PLAY
        };

        const button = (
          <ActionButton
            title="Actions"
            items={parsedInfo.state ? [ statusAction, invokeAction ] : [ invokeAction ]}
          />
        );

        return button;
      }
    });
  }, [
    terminology,
    updatePJS,
    theme,
    createProcessJobRequest
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          <Table
            ref={TableRef}
            tableOf="Scheduled jobs"
            error={processJobScheduleResponse.error}
            loading={loading}
            data={tableData && !processJobScheduleResponse.pending ? tableData : []}
            onFetchData={onFetchData}
            defaultPagination={defaultPagination}
            pageCount={processJobScheduleResponse.data?.pages ? processJobScheduleResponse.data?.pages : 0}
            rowCount={processJobScheduleResponse.data?.count ? processJobScheduleResponse.data?.count : 0}
            columns={columns}
            defaultVisibility={{
              jobParams: false,
              id: false
            }}

          />
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default ProcessJobSchedulePage;
